import React, { useState, useMemo } from "react";
import { useAdminStore } from "../stores/adminStore";
import { AddModal, Dropdown } from "components";
import { crmManagerLabel, customCrmManagerOption } from "./crmManagerOptions";

const INITIAL_FORM_STATE = {
  username: "",
  email: "",
  password: "",
  roleId: null,
  crmManagerId: null,
  teamLeadId: null,
};

// Компонент форми реєстрації користувача сторінки "Панель адміністратора"
const RegistrationForm = () => {
  // Отримуємо необхідні дані та дії зі стору адміністратора
  const { registerUser, roles, crmManagers } = useAdminStore.getState();
  const users = useAdminStore((state) => state.users);

  // Локальні стани для керування модальним вікном та формою
  const [isOpen, setIsOpen] = useState(false);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [formStatus, setFormStatus] = useState("idle");

  // Трансформація ролей для компонента Dropdown
  const roleOptions = useMemo(
    () => roles.map((role) => ({ value: role.id, label: role.name })),
    [roles],
  );

  // Універсальний обробник для полів вводу
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  // Спеціальний обробник для Dropdown (ролі)
  const handleRoleChange = (val) => {
    setFormState((prev) => ({ ...prev, roleId: val }));
  };

  // Менеджери CRM для Dropdown: колишні (blocked) підписані, 0 («—») — без прив'язки
  const crmManagerOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...crmManagers.map((m) => ({ value: m.id, label: crmManagerLabel(m) })),
    ],
    [crmManagers],
  );

  // Обробник для Dropdown (менеджер CRM): 0 означає null
  const handleCrmManagerChange = (val) => {
    setFormState((prev) => ({ ...prev, crmManagerId: val ? val : null }));
  };

  // Тімліди для Dropdown — будь-який наявний користувач; 0 («—») — без команди
  const teamLeadOptions = useMemo(
    () => [
      { value: 0, label: "—" },
      ...users
        .slice()
        .sort((a, b) => a.username.localeCompare(b.username, "uk"))
        .map((u) => ({ value: u.id, label: `${u.username} · ${u.id}` })),
    ],
    [users],
  );

  // Обробник для Dropdown (тімлід): 0 означає null
  const handleTeamLeadChange = (val) => {
    setFormState((prev) => ({ ...prev, teamLeadId: val ? val : null }));
  };

  // Функція закриття модального вікна
  const handleClose = () => {
    setFormState(INITIAL_FORM_STATE);
    setFormStatus("idle");
    setIsOpen(false);
  };

  // Валідація заповнення обов'язкових полів
  const isFormValid = useMemo(() => {
    const { username, email, password, roleId } = formState;
    return username.trim() && email.trim() && password.trim() && roleId !== null;
  }, [formState]);

  // Функція обробки відправлення форми
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setFormStatus("loading");
    try {
      const userData = {
        username: formState.username,
        email: formState.email,
        password: formState.password,
        role_id: parseInt(formState.roleId, 10),
        // Зв'язок із менеджером CRM — за ним VMS показує людині її штрафи
        crm_manager_id: formState.crmManagerId ? parseInt(formState.crmManagerId, 10) : null,
        // Чиїй команді належить — за цим тімлід бачить штрафи й порушення підопічних
        team_lead_id: formState.teamLeadId ? parseInt(formState.teamLeadId, 10) : null,
      };

      await registerUser(userData);
      setFormStatus("success");

      // При успіху закриваємо форму через невелику паузу для фідбеку
      setTimeout(handleClose, 1500);
    } catch (err) {
      console.error("Помилка реєстрації:", err);
      setFormStatus("error");
      setTimeout(() => setFormStatus("idle"), 2000);
    }
  };

  // Конфігурація станів головної кнопки
  const buttonConfigs = {
    loading: { text: "Реєстрація...", disabled: true },
    success: { text: "Створено!", disabled: true },
    error: { text: "Помилка!", disabled: true },
    idle: { text: "Зареєструвати", disabled: !isFormValid },
  };
  const currentBtn = buttonConfigs[formStatus];

  return (
    <>
      <button
        className="toggle-form-button focus-button"
        onClick={() => setIsOpen(true)}
      >
        Додати користувача
      </button>

      <AddModal isOpen={isOpen} onClose={handleClose} title="Реєстрація користувача">
        <form onSubmit={handleSubmit}>
          <div className="add-modal-body">
            <div className="form-group">
              <label>Ім'я користувача *</label>
              <input
                name="username"
                type="text"
                placeholder="Введіть ім'я"
                autoComplete="off"
                value={formState.username}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Email *</label>
              <input
                name="email"
                type="email"
                placeholder="example@mail.com"
                autoComplete="off"
                value={formState.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Пароль *</label>
              <input
                name="password"
                type="password"
                placeholder="Введіть пароль"
                autoComplete="new-password"
                value={formState.password}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Роль користувача *</label>
              <Dropdown
                options={roleOptions}
                selected={formState.roleId}
                onChange={handleRoleChange}
                mode="select"
                placeholder="Оберіть роль"
              />
            </div>

            <div className="form-group">
              <label>Менеджер CRM</label>
              <Dropdown
                options={crmManagerOptions}
                selected={formState.crmManagerId ?? 0}
                onChange={handleCrmManagerChange}
                mode="autocomplete"
                placeholder="ПІБ або id (можна той, якого ще немає в списку)"
                customOption={customCrmManagerOption}
                fixed
                listMinWidth={360}
              />
            </div>

            <div className="form-group">
              <label>Тімлід</label>
              <Dropdown
                options={teamLeadOptions}
                selected={formState.teamLeadId ?? 0}
                onChange={handleTeamLeadChange}
                mode="autocomplete"
                placeholder="Ім'я або id"
                fixed
                listMinWidth={280}
              />
            </div>
          </div>

          <div className="add-modal-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={formStatus === "loading"}
            >
              Відміна
            </button>
            <button type="submit" className="btn-save" disabled={currentBtn.disabled}>
              {currentBtn.text}
            </button>
          </div>
        </form>
      </AddModal>
    </>
  );
};

export default RegistrationForm;
